/**
 * Firebase Token Refresh Utility
 * 
 * Handles automatic token refresh when API requests fail with 401 errors.
 * This ensures users don't experience interruptions when Firebase tokens expire.
 */

import { auth } from '@/lib/firebase';

/**
 * Get a fresh Firebase token, forcing a refresh if necessary
 */
export async function getFreshToken(forceRefresh: boolean = false): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  
  const token = await user.getIdToken(forceRefresh);
  return token;
}

/**
 * Check if an error is a 401 Unauthorized error
 */
export function isUnauthorizedError(error: unknown): boolean {
  // Check for HTTP 401 status in various error formats
  if (error && typeof error === 'object') {
    const err = error as Record<string, unknown>;
    
    // Direct status property
    if (err.status === 401) {
      return true;
    }
    
    // Response object with status
    if (err.response && typeof err.response === 'object') {
      const response = err.response as Record<string, unknown>;
      if (response.status === 401) {
        return true;
      }
    }
    
    // ResponseError from OpenAPI client
    if (err.response instanceof Response) {
      return err.response.status === 401;
    }
  }
  
  return false;
}

/**
 * Wrapper function that automatically refreshes token on 401 errors and retries
 * 
 * @param apiCall - The API call function to execute
 * @param setTokenFn - Function to update the API client's token
 * @returns The result of the API call
 */
export async function withTokenRefresh<T>(
  apiCall: () => Promise<T>,
  setTokenFn: (token: string) => void
): Promise<T> {
  try {
    return await apiCall();
  } catch (error) {
    // If it's a 401 error, refresh token and retry once
    if (isUnauthorizedError(error)) {
      console.log('[Token Refresh] Detected 401 error, refreshing token...');
      
      try {
        // Get a fresh token (force refresh)
        const freshToken = await getFreshToken(true);
        
        // Update the API client with the new token
        setTokenFn(freshToken);
        
        console.log('[Token Refresh] Token refreshed, retrying request...');
        
        // Retry the API call with the new token
        return await apiCall();
      } catch (refreshError) {
        console.error('[Token Refresh] Failed to refresh token:', refreshError);
        // If refresh fails, throw the original error
        throw error;
      }
    }
    
    // If it's not a 401 error, just throw it
    throw error;
  }
}

/**
 * Get the expiration time of the current Firebase token
 * Returns null if user is not authenticated or token can't be decoded
 */
export function getTokenExpirationTime(): Date | null {
  const user = auth.currentUser;
  if (!user) {
    return null;
  }
  
  // Firebase tokens are JWTs - we can decode them to get expiration
  // The token is available via getIdTokenResult but that requires async
  // For now, we'll return null and implement proactive refresh differently
  return null;
}

/**
 * Check if the current token is about to expire (within the threshold)
 * 
 * @param thresholdMinutes - Minutes before expiration to consider "about to expire"
 * @returns true if token will expire within threshold, false otherwise
 */
export async function isTokenExpiringSoon(thresholdMinutes: number = 5): Promise<boolean> {
  const user = auth.currentUser;
  if (!user) {
    return false;
  }
  
  try {
    // Get token result which includes expiration time
    const tokenResult = await user.getIdTokenResult();
    const expirationTime = new Date(tokenResult.expirationTime);
    const now = new Date();
    const timeUntilExpiration = expirationTime.getTime() - now.getTime();
    const thresholdMs = thresholdMinutes * 60 * 1000;
    
    return timeUntilExpiration < thresholdMs;
  } catch (error) {
    console.error('[Token Refresh] Error checking token expiration:', error);
    return false;
  }
}

/**
 * Proactively refresh the token if it's about to expire
 * 
 * @param setTokenFn - Function to update the API client's token
 * @param thresholdMinutes - Minutes before expiration to refresh (default: 5)
 */
export async function refreshTokenIfNeeded(
  setTokenFn: (token: string) => void,
  thresholdMinutes: number = 5
): Promise<void> {
  try {
    const shouldRefresh = await isTokenExpiringSoon(thresholdMinutes);
    
    if (shouldRefresh) {
      console.log('[Token Refresh] Token expiring soon, proactively refreshing...');
      const freshToken = await getFreshToken(true);
      setTokenFn(freshToken);
      console.log('[Token Refresh] Token proactively refreshed');
    }
  } catch (error) {
    console.error('[Token Refresh] Error during proactive token refresh:', error);
    // Don't throw - proactive refresh failure shouldn't break the app
  }
}
